import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'
import { Typography } from '@/shared/ui/typography'

import { type FC, type ReactNode, useId, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import { matchPath, useLocation } from 'react-router-dom'

import type { SideMenuGroupProps, SideMenuSubItemProps } from './types'

type SideMenuGroupSlots = {
  SideMenuSubItem: SideMenuSubItemProps
}

const isCurrentPathInSubItems = (children: ReactNode, pathname: string): boolean => {
  const { SideMenuSubItem: subItems } = getTypedBlocksByTypes<
    SideMenuGroupSlots,
    ['SideMenuSubItem']
  >(children, ['SideMenuSubItem'])
  return subItems.some(child => {
    const { to, end, active } = child.props as SideMenuSubItemProps
    if (active) return true
    return to ? !!matchPath({ path: to, end: !!end }, pathname) : false
  })
}

/**
 * SideMenu.Group — a level-1 expandable item containing SubItems.
 *
 * The header toggles the expanded state on click. Multiple groups can be
 * open simultaneously. Auto-expands at mount if any SubItem matches the
 * current route (subsequent navigation does not re-trigger this).
 *
 * In the collapsed menu state (closed, not hovered), the SubItem list is
 * hidden via CSS and the chevron is not shown.
 */
export const SideMenuGroup: FC<SideMenuGroupProps> = ({
  icon,
  label,
  defaultExpanded,
  children,
}) => {
  const { pathname } = useLocation()
  const contentId = useId()

  // Active when one of the SubItems matches the current route. Computed on
  // every render (not memoized at mount) so the parent stays highlighted as
  // the user navigates between its SubItems — and, crucially, so the parent
  // icon signals the active section when the rail is collapsed and SubItems
  // are hidden.
  const isActive = isCurrentPathInSubItems(children, pathname)

  // Auto-expand is computed only at mount via useState initializer —
  // subsequent pathname changes must not force-expand the group again
  // (the user may have collapsed it intentionally).
  const [isExpanded, setIsExpanded] = useState(
    () => defaultExpanded ?? isCurrentPathInSubItems(children, pathname),
  )

  const toggleExpanded = () => setIsExpanded(prev => !prev)

  return (
    <div className="side-menu-group">
      <button
        type="button"
        className={cn(
          'side-menu-group-trigger',
          isExpanded && 'side-menu-group-expanded',
          isActive && 'side-menu-group-trigger-active',
        )}
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <span className="side-menu-item-content">
          <span className="side-menu-item-icon">{icon}</span>
          <span className="side-menu-item-label">
            <Typography type="paragraph" variant="md">
              {label}
            </Typography>
          </span>
        </span>
        <span className="side-menu-group-chevron">
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>
      {isExpanded && (
        <div id={contentId} className="side-menu-group-content">
          {children}
        </div>
      )}
    </div>
  )
}

SideMenuGroup.displayName = 'SideMenuGroup'
